import {
  TrayPredictionResponseDtoSchema,
  type TrayPredictionRequestDto,
  type TrayPredictionResponseDto,
} from '@peritasai/types';
import { getOemTrayApiUrl } from './oem-tray-api-url';

/** Resolves the OEM service base URL, or `null` when the real API is disabled. */
type BaseUrlResolver = () => string | null;

/**
 * Client for the OEM tray prediction microservice.
 *
 * Standalone service (not periverse-api): plain `fetch`, no bearer token, base URL
 * from {@link getOemTrayApiUrl}. Responses are validated against the DTO schema
 * before they cross into the app.
 */
export class OemTrayApiClient {
  constructor(private readonly resolveBaseUrl: BaseUrlResolver = getOemTrayApiUrl) {}

  /** Whether the real API is configured (env not set to an empty string). */
  isEnabled(): boolean {
    return this.resolveBaseUrl() !== null;
  }

  /**
   * `POST /predict` — send a tray photo, get back the tray name and per-slot
   * present/missing verdicts. Throws when the API is disabled so orchestrating
   * callers can decide to fall back to a mock.
   */
  async predict(
    request: TrayPredictionRequestDto,
    signal?: AbortSignal
  ): Promise<TrayPredictionResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    if (baseUrl === null) {
      throw new Error('OEM tray prediction API is disabled');
    }

    const response = await fetch(`${baseUrl.replace(/\/+$/, '')}/predict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(
        `OEM /predict failed: ${response.status} ${detail}`.trim()
      );
    }

    const parsed = TrayPredictionResponseDtoSchema.safeParse(
      await response.json()
    );
    if (!parsed.success) {
      throw new Error(
        `OEM /predict returned an unexpected shape: ${parsed.error.message}`
      );
    }
    return parsed.data;
  }
}

export const createOemTrayApiClient = (
  resolveBaseUrl: BaseUrlResolver = getOemTrayApiUrl
): OemTrayApiClient => new OemTrayApiClient(resolveBaseUrl);

export const oemTrayApiClient = new OemTrayApiClient();
